import { requireService, wrap } from '@repo/di-ez';
import { hasValue } from '@repo/utils';
import * as echarts from 'echarts';
import 'echarts-gl';
import { useEffect, useRef } from 'react';

import type { Trajectory3dLine, Trajectory3dViewControlState } from '../trajectory-graph.store';
import { formatGridLabel } from '../utils/format-chart-number';

import styles from './trajectory-3d-chart.module.scss';
import {
  disableNativeLine3DPicking,
  findNearbyTrajectoryLineHits,
  formatTrajectory3dTooltipHtml,
  showAxisPointerForClosestHit,
} from './trajectory-3d-multi-tooltip';

const CHART_COLOR = '#ffffff';
const CHART_COLOR_MUTED = 'rgba(255, 255, 255, 0.35)';
const VIEW_CONTROL_ROTATE_SENSITIVITY = 5;
/**
 * Diameter (in screen pixels) of the virtual hit circle around the cursor used to
 * detect nearby trajectory lines for the tooltip. Intentionally decoupled from the
 * rendered line width: it exists purely so the user doesn't need to aim precisely
 * at a thin line, and all lines whose on-screen projection falls inside this
 * circle are reported (not just the closest one), since several curves can look
 * close together on screen depending on the camera angle even if they are far
 * apart in 3D space.
 */
const TOOLTIP_HIT_DIAMETER = 24;

const axis3DStyle = {
  nameTextStyle: { color: CHART_COLOR },
  axisLine: { lineStyle: { color: CHART_COLOR } },
  axisTick: { lineStyle: { color: CHART_COLOR } },
  axisLabel: { color: CHART_COLOR },
  splitLine: { lineStyle: { color: CHART_COLOR_MUTED } },
};

function extractViewControl(chart: echarts.ECharts): Trajectory3dViewControlState | null {
  const option = chart.getOption() as { grid3D?: Array<{ viewControl?: Trajectory3dViewControlState }> };
  const viewControl = option.grid3D?.[0]?.viewControl;

  if (!viewControl) {
    return null;
  }

  const { alpha, beta, distance, center } = viewControl;

  return { alpha, beta, distance, center };
}

function buildChartOption(
  lines: Trajectory3dLine[],
  savedViewControl: Trajectory3dViewControlState | null,
): echarts.EChartsOption {
  const series = lines.map((line) => ({
    type: 'line3D' as const,
    name: `Trajectory_${line.label}`,
    data: line.data,
    shading: 'color' as const,
    silent: true,
    lineStyle: {
      width: line.lineWidth,
      color: line.color,
    },
    itemStyle: {
      color: line.color,
    },
  }));

  return {
    animation: false,
    textStyle: {
      color: CHART_COLOR,
    },
    tooltip: {
      show: false,
    },
    grid3D: {
      viewControl: {
        projection: 'perspective',
        autoRotate: false,
        distance: 200,
        ...savedViewControl,
        rotateSensitivity: VIEW_CONTROL_ROTATE_SENSITIVITY,
      },
      axisLine: {
        lineStyle: {
          color: CHART_COLOR,
        },
      },
      axisPointer: {
        // Disables echarts-gl's own "hover grid background wall" auto axis pointer,
        // which would otherwise fight with our custom hover logic below. Axis
        // pointer rendering itself stays available for our own dispatched actions
        // via the per-axis `axisPointer.show: true` overrides.
        show: false,
        lineStyle: {
          color: CHART_COLOR,
        },
      },
      splitLine: {
        lineStyle: {
          color: CHART_COLOR_MUTED,
        },
      },
      light: {
        main: {
          intensity: 1.2,
        },
        ambient: {
          intensity: 0.4,
        },
      },
    },
    xAxis3D: {
      type: 'value',
      name: 'WE',
      ...axis3DStyle,
      axisLabel: {
        ...axis3DStyle.axisLabel,
        formatter: (value: number) => formatGridLabel(value),
      },
      axisPointer: { show: true },
    },
    yAxis3D: {
      type: 'value',
      name: 'NS',
      ...axis3DStyle,
      axisLabel: {
        ...axis3DStyle.axisLabel,
        formatter: (value: number) => formatGridLabel(value),
      },
      axisPointer: { show: true },
    },
    zAxis3D: {
      type: 'value',
      name: 'TVD',
      ...axis3DStyle,
      axisLabel: {
        ...axis3DStyle.axisLabel,
        formatter: (value: number) => formatGridLabel(-value),
      },
      axisPointer: { show: true },
    },
    series,
  } as echarts.EChartsOption;
}

export const Trajectory3dChart = wrap(function Trajectory3dChart() {
  const graphStore = requireService('trajectoryGraphStore');
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<echarts.ECharts | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const isChartInitializedRef = useRef(false);
  const linesRef = useRef(graphStore.mainTrajectory3dLines);

  const chartLines = graphStore.mainTrajectory3dLines;
  linesRef.current = chartLines;

  useEffect(() => {
    const container = containerRef.current;

    if (!container) {
      return;
    }

    const chart = echarts.init(container);
    const tooltip = document.createElement('div');
    tooltip.className = `${styles.tooltip} caption`;
    tooltip.style.display = 'none';
    container.appendChild(tooltip);
    chartRef.current = chart;
    tooltipRef.current = tooltip;
    graphStore.registerChartInstance(chart);

    const hideTooltip = () => {
      tooltip.style.display = 'none';
      chart.dispatchAction({ type: 'grid3DHideAxisPointer' });
    };

    const handleMouseMove = (event: { offsetX?: number; offsetY?: number }) => {
      const mouseX = event.offsetX;
      const mouseY = event.offsetY;

      if (!hasValue(mouseX) || !hasValue(mouseY)) {
        hideTooltip();
        return;
      }

      const hits = findNearbyTrajectoryLineHits(chart, linesRef.current, mouseX, mouseY, TOOLTIP_HIT_DIAMETER / 2);

      if (!hits.length) {
        hideTooltip();
        return;
      }

      tooltip.innerHTML = formatTrajectory3dTooltipHtml(hits);
      tooltip.style.display = 'block';
      tooltip.style.left = `${mouseX + 12}px`;
      tooltip.style.top = `${mouseY + 12}px`;

      showAxisPointerForClosestHit(chart, hits);
    };

    const persistViewControl = () => {
      const viewControl = extractViewControl(chart);

      if (viewControl) {
        graphStore.setTrajectory3dViewControl(viewControl);
      }
    };

    const zr = chart.getZr();
    zr.on('mousemove', handleMouseMove);
    zr.on('globalout', hideTooltip);
    zr.on('mouseup', persistViewControl);
    zr.on('mousewheel', persistViewControl);

    const resizeObserver = new ResizeObserver(() => {
      chart.resize();
      disableNativeLine3DPicking(chart);
    });

    resizeObserver.observe(container);

    return () => {
      persistViewControl();
      zr.off('mousemove', handleMouseMove);
      zr.off('globalout', hideTooltip);
      zr.off('mouseup', persistViewControl);
      zr.off('mousewheel', persistViewControl);
      resizeObserver.disconnect();
      tooltip.remove();
      tooltipRef.current = null;
      graphStore.unregisterChartInstance(chart);
      chart.dispose();
      chartRef.current = null;
      isChartInitializedRef.current = false;
    };
  }, [graphStore]);

  useEffect(() => {
    const chart = chartRef.current;

    if (!chart) {
      return;
    }

    if (!isChartInitializedRef.current) {
      chart.setOption(buildChartOption(chartLines, graphStore.trajectory3dViewControl));
      isChartInitializedRef.current = true;
      disableNativeLine3DPicking(chart);
      return;
    }

    chart.setOption({
      series: chartLines.map((line) => ({
        data: line.data,
        lineStyle: {
          width: line.lineWidth,
          color: line.color,
        },
        itemStyle: {
          color: line.color,
        },
      })),
    });
    disableNativeLine3DPicking(chart);
  }, [chartLines, graphStore]);

  return (
    <div className={styles.root}>
      <div ref={containerRef} className={styles.chart} />
    </div>
  );
});
